import { Paper, styled, Typography } from "@mui/material";
import { Map, GeoJSON } from "leaflet";
import { useTranslation } from "react-i18next";

import { useMeasuringMode } from "hooks/measuringHooks";

import {
  DrawType,
  MeasureImpact,
  MeasureConfLayer,
  MeasureConf,
  DrawState,
} from "types/types";

import { AddNewPolygon } from "./AddNewPolygon";
import { ActiveMeasureConfigs } from "./ActiveMeasureConfigs";
import { AddNewMarker } from "./AddNewMarker";

interface Props {
  mapRef: Map;
  selectedMeasure?: MeasureImpact;
  measureConfigurations: MeasureConf[];
  measureConfigId: number;
  basePolygon: GeoJSON;
  radius: number;
  minArea: number;
  drawState: DrawState;
  updateDrawState: (drawType?: DrawType) => void;
  addNewGeometryToMeasureConfig: (
    newLayer: MeasureConfLayer,
    updatedMeasureConfigs: MeasureConf[]
  ) => void;
  removeLayerFromMeasureConfig: (layerId: number) => void;
  updateMeasureConfigurations: (updatedMeasureConfigs: MeasureConf[]) => void;
}

export function DrawMeasureSection(props: Props) {
  const {
    basePolygon,
    mapRef,
    selectedMeasure,
    measureConfigurations,
    measureConfigId,
    minArea,
    radius,
    drawState,
    updateDrawState,
    addNewGeometryToMeasureConfig,
    removeLayerFromMeasureConfig,
    updateMeasureConfigurations,
  } = props;
  const { t } = useTranslation();
  const { isMeasuring } = useMeasuringMode();

  return (
    <StyledPaper elevation={0}>
      <Typography variant="h4-green" component="h2">
        {t("label.drawYourMeasureOnTheMap")}
      </Typography>

      <ActiveMeasureConfigs
        measureConfigId={measureConfigId}
        mapRef={mapRef}
        measureConfigurations={measureConfigurations}
        basePolygon={basePolygon}
        minArea={minArea}
        drawState={drawState}
        removeLayerFromMeasureConfig={removeLayerFromMeasureConfig}
        updateMeasureConfigurations={updateMeasureConfigurations}
        updateDrawState={updateDrawState}
      />

      {!isMeasuring &&
        selectedMeasure &&
        selectedMeasure.measure.geometryType === "POLYGON" && (
          <AddNewPolygon
            drawState={drawState}
            mapRef={mapRef}
            selectedMeasure={selectedMeasure}
            measureConfigurations={measureConfigurations}
            basePolygon={basePolygon}
            minArea={minArea}
            updateDrawState={updateDrawState}
            addNewGeometryToMeasureConfig={addNewGeometryToMeasureConfig}
          />
        )}
      {!isMeasuring &&
        selectedMeasure &&
        selectedMeasure.measure.geometryType === "POINT" && (
          <AddNewMarker
            mapRef={mapRef}
            measureImpact={selectedMeasure}
            studyArea={basePolygon}
            measureConfigurations={measureConfigurations}
            addNewGeometryToMeasureConfig={addNewGeometryToMeasureConfig}
            radius={radius}
          />
        )}
    </StyledPaper>
  );
}

const StyledPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  overflow: auto;
`;
